import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";

import { CoursesResponse } from "~/entity/Courses";
import { IParamsCourses } from "~/entity/Courses";
import { CoursesService } from "~/entity/Courses/api/service";

type TUseGetAllCoursesForSelectOption = {
  customHandler?: (course: CoursesResponse) => {
    label: string;
    value: string | number;
  };
  initialParams?: Partial<IParamsCourses>;
  pageSize?: number;
};

export const useGetAllCoursesForSelect = (
  options?: TUseGetAllCoursesForSelectOption,
) => {
  const { customHandler, initialParams, pageSize = 25 } = options || {};
  const stableInitialParams = useMemo(
    () => initialParams ?? {},
    [initialParams],
  );

  const [courses, setCourses] = useState<
    { label: string; value: string | number }[]
  >([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState<string | null>(null);

  const formatCourse = useCallback(
    (course: CoursesResponse) => {
      if (customHandler) return customHandler(course);
      return {
        label: `${course.codeIS} ${course.name}`,
        value: course.instanceId,
      };
    },
    [customHandler],
  );

  const loadCourses = useCallback(
    async (page: number, search: string | null = null) => {
      setLoading(true);
      try {
        const today = new Date();
        const actualOn = today.toISOString().split("T")[0];

        const params: IParamsCourses = {
          page,
          count: pageSize,
          searchText: search ?? undefined,
          actualOn,
          ...stableInitialParams,
        };

        const response = await CoursesService.GET.getCourses(params);
        const items = response.data.courses || [];

        const formatted = items.map(formatCourse);

        if (page === 0) {
          setCourses([{ value: undefined, label: "Не выбрано" }, ...formatted]);
        } else {
          setCourses((prev) => [...prev, ...formatted]);
        }

        setHasMore(items.length === pageSize);
        setCurrentPage(page);
      } catch (error) {
        console.error("Failed to load courses", error);
        setHasMore(false);
      } finally {
        setLoading(false);
      }
    },
    [formatCourse, stableInitialParams, pageSize],
  );

  useEffect(() => {
    if (searchTerm !== undefined) {
      loadCourses(0, searchTerm);
    }
  }, [loadCourses, searchTerm]);

  const onScrollBottomCourses = useCallback(() => {
    if (!hasMore || loading) return;
    loadCourses(currentPage + 1, searchTerm);
  }, [hasMore, loading, currentPage, searchTerm, loadCourses]);

  const debouncedSearch = useCallback(
    debounce((search: string | null) => {
      setSearchTerm(search);
    }, 300),
    [],
  );

  const searchCourses = useCallback(
    (search: string | null = null) => {
      debouncedSearch(search || null);
    },
    [debouncedSearch],
  );

  return {
    courses,
    searchCourses,
    onScrollBottomCourses,
    loading,
  };
};
